import { SignupPaymentForm } from '@src/components/sco/Account/SignupPaymentForm'
import type { FormContainerProps } from '@src/components/sco/FormContainer'
import type { SubmitCheckoutPayload } from '@src/graphql-types'

import type { ConversionOfferSelection } from '../decision'

export type ConversionPaymentStepProps = {
  /** Exactly what is charged: product, term, and an API-applied coupon code (never a raw entry). */
  selection: ConversionOfferSelection
  intent?: string | null
  /** Called after `submitCheckout` succeeds and the viewer has been refetched. */
  onSuccess?: (payload: SubmitCheckoutPayload) => void | Promise<void>
  onError?: (error: unknown) => void
  /** Full-page checkout redirects here after success instead of calling back. */
  returnToHref?: string
  /** Show the form's own price block (pages without a Conversion summary beside it). */
  showPricing?: boolean
}

/** The payment form sits flush in its checkout pane; the pane, not a nested card, is the surface. */
export const FLUSH_PAYMENT_FORM_CONTAINER: FormContainerProps = {
  background: 'transparent',
  border: 0,
  borderRadius: 0,
  boxShadow: 'none',
  height: 'full',
  paddingX: { base: 5, md: 8 },
  paddingY: { base: 5, md: 8 },
}

/**
 * The Conversion payment step: guest registration or login, confirm-info, the Stripe Payment
 * Element, 3DS resume, and `submitCheckout`, behind a props-in contract. It wraps the proven
 * `SignupPaymentForm` so every checkout charges through one path.
 */
export const ConversionPaymentStep = ({
  intent,
  onError,
  onSuccess,
  returnToHref,
  selection,
  showPricing = false,
}: ConversionPaymentStepProps) => (
  <SignupPaymentForm
    key={`${selection.productId}:${selection.termId || ''}:${selection.couponCode || ''}`}
    afterError={(_response, error) => onError?.(error)}
    afterSuccess={payload => onSuccess?.(payload)}
    containerProps={FLUSH_PAYMENT_FORM_CONTAINER}
    couponCode={selection.couponCode || undefined}
    hidePricing={!showPricing}
    hideProductTitle={!showPricing}
    intent={intent}
    paymentProvider={'stripe-methods'}
    productId={selection.productId}
    returnToHref={returnToHref}
    termId={selection.termId || undefined}
  />
)
